<template>
  <div class="py-4 container-fluid">
    <div class="row justify-content-center">
      <div v-if="loaded" class="col-lg-6 col-md-8">
        <card :title="$t('userSettings.generalSection')" class="mb-4">
          <label>{{ $t('users.language') }}</label>
          <el-select v-model="language" class="w-100" :class="selectClass" :placeholder="$t('users.selectLanguage')">
            <el-option v-for="(info, code) in languageMap" :key="code" :class="selectClass" :label="`${info.flag} ${info.name}`" :value="code" />
          </el-select>
          <!-- Which screen opens with the app; the customer's default unless chosen here -->
          <div v-if="startPageAvailable" class="mt-3">
            <label>{{ $t('users.startPage') }}</label>
            <el-select v-model="defaultAppScreen" class="w-100" :class="selectClass">
              <el-option :class="selectClass" :label="$t('users.startPageCompanyDefault')" :value="companyDefaultScreen" />
              <el-option :class="selectClass" :label="$t('sidebar.timeRegistration')" value="time-registration" />
              <el-option :class="selectClass" :label="$t('tools.myTools')" value="mytools" />
            </el-select>
          </div>
        </card>

        <!-- Only for customers with the reference on (end-of-day registration) -->
        <card v-if="referenceAvailable" :title="$t('userSettings.timeRegistrationSection')" class="mb-4">
          <material-checkbox v-model="enableReference">{{ $t('timeRegistration.enableReferenceInTimeRegistration') }}</material-checkbox>
        </card>

        <card :title="$t('notifications.title')" class="mb-4">
          <div class="mb-2">
            <material-checkbox v-model="notificationSettings.emailEnabled">{{ $t('notifications.email') }}</material-checkbox>
          </div>
          <div v-if="pushAvailable" class="mb-2">
            <material-checkbox v-model="notificationSettings.pushEnabled">{{ $t('notifications.push') }}</material-checkbox>
          </div>
          <div v-if="isAdmin" class="mb-2">
            <material-checkbox v-model="notificationSettings.receiveAdminNotifications">{{ $t('notifications.admin') }}</material-checkbox>
          </div>
        </card>

        <div class="settings-actions d-flex justify-content-end">
          <material-button fill color="dark" type="standard" :disabled="saving" @click="save">{{ $t('common.save') }}</material-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { ElOption, ElSelect } from "element-plus";
import api from "../../services/api";
import config from "../../config";
import { ensurePushPermission, subscribeToPush, unsubscribeFromPush, isPushConfigured } from "../../services/pushNotifications";
import Card from "../../components/Card.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialCheckbox from "../../components/MaterialCheckbox.vue";
import { getCustomerConfig } from "./customerConfigHelper";
import { getUserConfigTypeId, getUserConfigValue, loadUserConfigs } from "./userConfigHelper";

// el-select treats null as nothing selected, so the "no own choice" option needs a value of its own
const COMPANY_DEFAULT_SCREEN = "company-default";

/**
 * UserSettings — the signed-in user's own settings, one card per section: language (PUT api/User/{id}),
 * start page (api/User/me/default-app-screen), user configurations (api/User/me/configurations) and
 * notification channels (api/Notification/settings/{userId}).
 */
export default {
  name: "UserSettings",
  components: { Card, ElOption, ElSelect, MaterialButton, MaterialCheckbox },
  data() {
    const token = jwtDecode(Cookies.get("accessToken"));
    return {
      userId: token.UserIdentifier,
      isAdmin: token.role !== "User",
      loaded: false,
      saving: false,
      referenceAvailable: false,
      enableReference: false,
      language: "",
      startPageAvailable: false,
      companyDefaultScreen: COMPANY_DEFAULT_SCREEN,
      defaultAppScreen: COMPANY_DEFAULT_SCREEN,
      languageMap: {
        'en-US': { name: 'English', flag: '🇺🇸' },
        'is-IS': { name: 'Íslenska', flag: '🇮🇸' },
        'pl-PL': { name: 'Polski', flag: '🇵🇱' },
        'lt-LT': { name: 'Lietuvis', flag: '🇱🇹' },
        'es-ES': { name: 'Español', flag: '🇪🇸' },
        'ro-RO': { name: 'Română', flag: '🇷🇴' },
        'sl-SI': { name: 'Slovenščina', flag: '🇸🇮' },
        'sk-SK': { name: 'Slovenčina', flag: '🇸🇰' }
      },
      notificationSettings: {
        emailEnabled: true,
        pushEnabled: true,
        receiveAdminNotifications: false,
        pushConfigured: false,
        apnsConfigured: false,
        pushPublicKey: null,
      },
    };
  },
  computed: {
    // Push can reach this device: Web Push (VAPID) in browsers, APNs in the iOS app.
    pushAvailable() { return isPushConfigured(this.notificationSettings); },
    selectClass() { return localStorage.getItem("darkMode") === "true" ? "select-primary" : "select-standard"; },
  },
  async created() {
    try {
      const [user, timeRegistration, endOfDay, reference, notifications] = await Promise.all([
        api.get(`/User/${this.userId}`),
        getCustomerConfig("EnableTimeRegistration"),
        getCustomerConfig("EnableEndOfDayTimeRegistration"),
        getCustomerConfig("EnableReferenceInTimeRegistration"),
        api.get(`/Notification/settings/${this.userId}`),
      ]);
      this.language = user.data.language;
      this.notificationSettings = notifications.data;
      this.startPageAvailable = timeRegistration === true;
      if (this.startPageAvailable) {
        const response = await api.get("/User/me/default-app-screen");
        this.defaultAppScreen = response.data.defaultAppScreen ?? COMPANY_DEFAULT_SCREEN;
      }
      this.referenceAvailable = endOfDay === true && reference === true;
      if (this.referenceAvailable) {
        // Not shown as off when unknown: saving that would overwrite the real value
        const configs = await loadUserConfigs();
        if (!configs) throw new Error("User configurations could not be loaded");
        this.enableReference = getUserConfigValue(configs, "EnableReferenceInTimeRegistration") === true;
      }
      this.loaded = true;
    } catch (error) {
      console.error("API request error:", error); // eslint-disable-line no-console
      this.$toast.danger(this.$t("common.unableToRetrieveData"), { timeout: 2000 });
    }
  },
  methods: {
    async save() {
      const { emailEnabled, pushEnabled, receiveAdminNotifications, pushPublicKey } = this.notificationSettings;
      // iOS/Safari only shows the notification permission prompt during a user gesture, and the
      // API awaits below consume it — so prompt first. subscribeToPush() handles a denial.
      if (this.pushAvailable && pushEnabled) await ensurePushPermission().catch(() => {});

      this.saving = true;
      try {
        // Accounts can have no language yet: nothing to save until one is picked
        if (this.language) {
          await api.put(`/User/${this.userId}`, { language: this.language });
          Cookies.set("locale", this.language, { secure: config.secureCookies, httpOnly: false });
          this.$i18n.locale = this.language;
        }
        if (this.startPageAvailable) {
          const defaultAppScreen = this.defaultAppScreen === COMPANY_DEFAULT_SCREEN ? null : this.defaultAppScreen;
          await api.put("/User/me/default-app-screen", { defaultAppScreen });
        }
        await api.put(`/Notification/settings/${this.userId}`, { emailEnabled, pushEnabled, receiveAdminNotifications });
        if (this.referenceAvailable) {
          await api.put("/User/me/configurations", {
            configurations: [{ userConfigurationTypeId: getUserConfigTypeId("EnableReferenceInTimeRegistration"), value: String(this.enableReference) }],
          });
          await loadUserConfigs(); // refreshes the cache, so the change applies at once
        }
        if (this.pushAvailable) await this.applyPushSubscription(pushEnabled, pushPublicKey);
        this.$toast.success(this.$t("userSettings.saved"), { timeout: 1500 });
      } catch (error) {
        console.error("API request error:", error); // eslint-disable-line no-console
        this.$toast.danger(this.$t("common.unableToUpdate"), { timeout: 2000 });
      } finally {
        this.saving = false;
      }
    },
    /** Push subscriptions are per device. */
    async applyPushSubscription(pushEnabled, publicKey) {
      if (!pushEnabled) {
        await unsubscribeFromPush();
        return;
      }
      try {
        await subscribeToPush(publicKey);
      } catch (error) {
        if (error.message === "permission-denied") {
          this.$toast.warning(this.$t("notifications.pushPermissionDenied"), { timeout: 3000 });
        } else if (error.message === "unsupported" || error.message === "no-service-worker") {
          this.$toast.warning(this.$t("notifications.pushUnsupported"), { timeout: 3000 });
        } else {
          throw error;
        }
      }
    },
  },
};
</script>

<style scoped>
.settings-actions { margin-right: 20px; } /* lines up with the cards' margin */
:deep(.card-body) { padding-top: 0; } /* the card header's padding is gap enough */
</style>
